import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';

const apiClient = axios.create({
  baseURL: '/api/v1',
});

/** Renew the access token when it expires within this many seconds. */
export const REFRESH_MARGIN_SECONDS = 30;

export interface RefreshedToken {
  token: string;
  /** Epoch seconds, as in oidc-client-ts `User.expires_at`. */
  expiresAt?: number;
}

type RefreshCallback = (staleToken: string | null) => Promise<RefreshedToken>;

let _accessToken: string | null = null;
let _accessTokenExpiresAt: number | undefined;
let _refreshCallback: RefreshCallback | null = null;
let _sessionExpiredCallback: (() => void) | null = null;
let _refreshPromise: Promise<RefreshedToken> | null = null;

export function setAccessToken(token: string | null, expiresAt?: number): void {
  _accessToken = token;
  _accessTokenExpiresAt = expiresAt;
}

export function setRefreshCallback(fn: RefreshCallback | null): void {
  _refreshCallback = fn;
}

export function setSessionExpiredCallback(fn: (() => void) | null): void {
  _sessionExpiredCallback = fn;
}

export function resetRefreshPromise(): void {
  _refreshPromise = null;
}

/** 401, or the legacy 500 the API returned for expired Keycloak tokens. 403 is never retryable. */
export function isAuthRetryable(error: unknown): boolean {
  const response = (error as AxiosError | undefined)?.response;
  if (!response) return false;
  if (response.status === 401) return true;
  // TODO remove after workers-api returns 401 for expired tokens (branch fix/expired-token-401).
  const message = (response.data as { message?: unknown } | undefined)?.message;
  return response.status === 500 && typeof message === 'string' && /\[Keycloak Guard\] Expired token/i.test(message);
}

// Single-flight: concurrent callers share one renewal.
function refreshOnce(): Promise<RefreshedToken> {
  if (!_refreshPromise) {
    const refresh = _refreshCallback as RefreshCallback;
    _refreshPromise = refresh(_accessToken)
      .then((refreshed) => {
        setAccessToken(refreshed.token, refreshed.expiresAt);
        return refreshed;
      })
      .finally(() => {
        _refreshPromise = null;
      });
  }
  return _refreshPromise;
}

// OAuth errors (e.g. invalid_grant) end the session; network errors do not.
function handleRefreshFailure(err: unknown): void {
  const isNetworkError = !(err as Record<string, unknown>)?.response &&
    !(err as Record<string, unknown>)?.error;
  if (!isNetworkError) {
    _sessionExpiredCallback?.();
  }
}

apiClient.interceptors.request.use(async (config) => {
  if (_accessToken && _refreshCallback && _accessTokenExpiresAt !== undefined) {
    const secondsLeft = _accessTokenExpiresAt - Date.now() / 1000;
    if (secondsLeft < REFRESH_MARGIN_SECONDS) {
      try {
        await refreshOnce();
      } catch (err) {
        // Never send a token that is known to be expired.
        if (secondsLeft <= 0) {
          handleRefreshFailure(err);
          throw err;
        }
      }
    }
  }
  if (_accessToken) {
    config.headers['Authorization'] = `Bearer ${_accessToken}`;
  }
  return config;
});

type RetryableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetryableConfig | undefined;
    if (!isAuthRetryable(error) || !config || config._retry) {
      return Promise.reject(error);
    }
    config._retry = true;
    if (_refreshCallback) {
      try {
        const { token } = await refreshOnce();
        config.headers['Authorization'] = `Bearer ${token}`;
        return apiClient(config);
      } catch (err) {
        handleRefreshFailure(err);
      }
    } else {
      _sessionExpiredCallback?.();
    }
    return Promise.reject(error);
  },
);

export default apiClient;
